import { useEffect, useRef, useState, useCallback } from "react";
import {
  Brain, Play, Loader2, X, AlertTriangle, Activity, Crosshair, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const scoreColor = (s) => (s >= 0.7 ? "text-red-600 bg-red-50" : s >= 0.4 ? "text-amber-600 bg-amber-50" : "text-teal-600 bg-teal-50");
const SCORE_LABELS = {
  score_malignite: "Score de malignité",
  densite_cellulaire: "Densité cellulaire",
  index_proliferation: "Index de prolifération",
};
const RUNNING = ["en_attente", "en_cours"];

export function AIPanel({ wsiId, demandeId, canLaunch, mobile, onRegions, onFocusRegion }) {
  const [models, setModels] = useState([]);
  const [selected, setSelected] = useState("");
  const [seuil, setSeuil] = useState(0.7);
  const [analysis, setAnalysis] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const startRef = useRef(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const applyResult = useCallback((a) => {
    setAnalysis(a);
    if (a?.statut === "termine" && a.resultats_json) {
      onRegions && onRegions(a.resultats_json.regions_interet || []);
    }
  }, [onRegions]);

  const poll = useCallback((id) => {
    stopPoll();
    startRef.current = Date.now();
    pollRef.current = setInterval(async () => {
      setElapsed(Math.round((Date.now() - startRef.current) / 1000));
      try {
        const { data } = await api.get(`/analyses/${id}`);
        setAnalysis(data);
        if (!RUNNING.includes(data.statut)) {
          stopPoll();
          applyResult(data);
          if (data.statut === "termine") toast.success("Analyse IA terminée");
          if (data.statut === "erreur") toast.error("Analyse IA en erreur");
        }
      } catch { stopPoll(); }
    }, 3000);
  }, [applyResult]);

  // Chargement initial : modèles + dernière analyse (en cours ou terminée)
  useEffect(() => {
    (async () => {
      try {
        const { data: ms } = await api.get("/modeles-ia");
        setModels(ms); if (ms[0]) setSelected(ms[0].id);
      } catch (e) { /* rôle sans accès IA */ }
      try {
        const { data: list } = await api.get(`/wsi/${wsiId}/ai-analyses`);
        const running = list.find((a) => RUNNING.includes(a.statut));
        const done = list.find((a) => a.statut === "termine");
        if (running) { setAnalysis(running); poll(running.id); }
        else if (done) applyResult(done);
      } catch { /* noop */ }
    })();
    return stopPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsiId]);

  const launch = async () => {
    if (!checked) { toast.error("Veuillez confirmer l'avertissement"); return; }
    setConfirmOpen(false); setChecked(false);
    try {
      const { data } = await api.post(`/wsi/${wsiId}/ai-analyses`, {
        modele_id: selected, demande_id: demandeId, parametres: { seuil: Number(seuil), zone: "lame_entiere" },
      });
      setAnalysis({ id: data.analysis_id, statut: "en_attente", progress: 0 });
      poll(data.analysis_id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cancel = async () => {
    if (!analysis) return;
    try { await api.post(`/analyses/${analysis.id}/cancel`); stopPoll(); setAnalysis(null); onRegions && onRegions([]); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const running = analysis && RUNNING.includes(analysis.statut);
  const result = analysis?.statut === "termine" ? analysis.resultats_json : null;
  const model = models.find((m) => m.id === selected);

  return (
    <div className={mobile ? "" : "bg-white border border-slate-200 rounded-xl p-3"} data-testid="wsi-ai-panel">
      <div className="font-semibold text-slate-800 mb-2 flex items-center gap-1.5 text-sm">
        <Brain className="h-4 w-4 text-violet-600" /> Analyse IA
        <span className="ml-auto text-[10px] font-normal text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">Aide à la décision</span>
      </div>

      {canLaunch && !running && (
        <div className="space-y-2">
          <select value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="wsi-ai-model-select"
                  className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm">
            {models.map((m) => <option key={m.id} value={m.id}>{m.nom} v{m.version} · {m.type_tache}</option>)}
          </select>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>Seuil</span>
            <input type="range" min="0.1" max="0.95" step="0.05" value={seuil}
                   onChange={(e) => setSeuil(e.target.value)} className="flex-1" data-testid="wsi-ai-seuil" />
            <span className="font-mono w-8 text-right">{Number(seuil).toFixed(2)}</span>
          </div>
          <button onClick={() => setConfirmOpen(true)} disabled={!selected} data-testid="wsi-ai-launch"
                  className="w-full py-2 bg-violet-600 text-white rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-violet-700 disabled:opacity-50">
            <Play className="h-4 w-4" /> Lancer l'analyse
          </button>
        </div>
      )}

      {running && (
        <div className="space-y-2" data-testid="wsi-ai-running">
          <div className="flex items-center gap-2 text-sm text-violet-700">
            <Loader2 className="h-4 w-4 animate-spin" />
            {analysis.statut === "en_attente" ? "En file d'attente…" : (analysis.message || "Analyse en cours…")}
          </div>
          <div className="h-2 bg-violet-100 rounded-full overflow-hidden">
            <div className="h-full bg-violet-600 transition-all" style={{ width: `${analysis.progress || 5}%` }} />
          </div>
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>Écoulé : {elapsed}s</span>
            {analysis.statut === "en_attente" && (
              <button onClick={cancel} data-testid="wsi-ai-cancel" className="text-red-500 hover:underline flex items-center gap-1">
                <X className="h-3 w-3" /> Annuler
              </button>
            )}
          </div>
        </div>
      )}

      {analysis?.statut === "erreur" && (
        <div className="text-xs text-red-600 bg-red-50 rounded-lg p-2 mt-1" data-testid="wsi-ai-error">
          Erreur : {analysis.erreur_message || "inconnue"}
        </div>
      )}

      {result && (
        <div className="space-y-3 mt-1" data-testid="wsi-ai-result">
          <div className="grid grid-cols-1 gap-1.5">
            {Object.entries(result.scores || {}).map(([k, v]) => (
              <div key={k} className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs ${scoreColor(v)}`}>
                <span className="font-medium">{SCORE_LABELS[k] || k}</span>
                <span className="font-mono font-bold">{Math.round(v * 100)}%</span>
              </div>
            ))}
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1">
              <Crosshair className="h-3.5 w-3.5" /> Régions d'intérêt ({(result.regions_interet || []).length})
            </div>
            <div className="space-y-1" data-testid="wsi-ai-roi-list">
              {(result.regions_interet || []).length === 0 && (
                <p className="text-[11px] text-slate-400">Aucune région au-dessus du seuil</p>
              )}
              {(result.regions_interet || []).map((r) => (
                <button key={r.id} onClick={() => onFocusRegion && onFocusRegion(r.geometrie.coords)}
                        data-testid={`wsi-ai-roi-item-${r.id}`}
                        className="w-full flex items-center justify-between px-2 py-1 rounded-lg text-xs hover:bg-slate-50 border border-slate-100">
                  <span className="flex items-center gap-1.5"><Activity className="h-3 w-3 text-orange-500" /> {r.label}</span>
                  <span className="font-mono font-semibold text-orange-600">{Math.round(r.score * 100)}%</span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 rounded-lg p-2" data-testid="wsi-ai-disclaimer">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <span>{result.avertissement || "Aide à la décision — le diagnostic final relève du pathologiste."}</span>
          </div>
        </div>
      )}

      {/* Modal de confirmation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="wsi-ai-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-violet-600" /> Lancer l'analyse IA</DialogTitle>
            <DialogDescription>{model ? `${model.nom} v${model.version} · durée estimée 2–5 s` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 rounded-lg p-3">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>Cette analyse est une <b>aide à la décision</b>. Le diagnostic final relève du pathologiste. Le résultat ne constitue pas un diagnostic autonome.</span>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} data-testid="wsi-ai-confirm-check" />
              J'ai lu et compris l'avertissement
            </label>
            <button onClick={launch} disabled={!checked} data-testid="wsi-ai-confirm-launch"
                    className="w-full py-2.5 bg-violet-600 text-white rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-violet-700 disabled:opacity-50">
              <Play className="h-4 w-4" /> Confirmer et lancer
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
